import { Directive, Host, OnDestroy } from '@angular/core';
import { PermissionService } from '@app/services/permission/permission.service';
import { combineLatest, of, Subscription } from 'rxjs';
import { SplitbuttonComponent, SplitButtonElement } from '@app/components/elements/splitbutton/splitbutton.component';
import { WorkflowItemNavigationService } from '@workflow/services/workflow-item-navigation.service';
import { Router } from '@angular/router';
import { EWorkflowPathKey } from '@app/pages/participant/workflow/workflow-path-key.enum';
import { CustomState, CustomStateTransition, IWorkflowItem } from '@interfaces/interfaces';
import { WorkflowItemDirective } from '@workflow/directives/workflow-item/workflow-item.directive';
import { map, switchMap } from 'rxjs/operators';
import { ProjectService } from '@app/services/project/project.service';
import { participantPages } from '@app/pages/participant/participants.routes';
import { ConfirmDialog } from '@app/services/confirm-dialog/confirm-dialog.service';
import { TranslateService } from '@ngx-translate/core';

@Directive({
	selector: '[appEndWorkflowItem]',
})
export class EndWorkflowItemDirective implements OnDestroy {
	
	private readonly fallBackElement: SplitButtonElement = { title: 'workflow.endView', icon: 'check', type: 'primary', callback: () => null };
	
	private subscriptions = new Subscription();
	
	constructor(
		@Host() hostSplitButton: SplitbuttonComponent,
		private readonly workflowItemDirective: WorkflowItemDirective,
		permissionService: PermissionService,
		projectService: ProjectService,
		private readonly workflowNavigationService: WorkflowItemNavigationService,
		private readonly router: Router,
		private readonly translateService:TranslateService,
		private readonly confirmDialog: ConfirmDialog
	) {
		const isWfsCompletable$ = combineLatest([
			projectService.hasWorkflowWriteOnActiveProject$,
			workflowItemDirective.workflowStatus$.pipe(map(status => !status?.completed && !!status?.started)),
			workflowItemDirective.workflowItem$,
		]).pipe(
			map(([hasWritePermission, isWfsOngoing, wfItem]) => hasWritePermission && isWfsOngoing && !!wfItem?.id && !wfItem?.customStates?.length),
		);
		const availableTransitions$ = combineLatest([
			workflowItemDirective.workflowStatus$,
			workflowItemDirective.workflowItem$,
		]).pipe(
			map(([wfsStatus, wfsItem]) => (wfsItem?.customStateTransitions?.filter(t =>
				t.startStateId === wfsStatus?.customState &&
				wfsItem?.customStates?.some(item => item.id === t.endStateId),
			) || []) as CustomStateTransition[]),
			switchMap(availableTransitions => {
				if (availableTransitions.length)
					return combineLatest(
						availableTransitions.map(transition => permissionService.hasAnyPermissions$(...transition.requiredPermissions).pipe(map(p => p ? transition : null))),
					).pipe(map(transitionOrNull => transitionOrNull.filter(t => !!t)));
				
				return of([]);
			}),
		);
		
		this.subscriptions.add(
			combineLatest([
				workflowItemDirective.workflowItem$,
				isWfsCompletable$,
				availableTransitions$,
			]).pipe(
				map(([wfsItem, isWfsCompletable, availableTransitions]) => this.mapWfsItemStatusToElements(isWfsCompletable, wfsItem, availableTransitions)),
			).subscribe(elements => hostSplitButton.elements = elements),
		);
	}
	
	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}
	
	private backToUpperLevel(wfsItem: IWorkflowItem): void {
		this.router
			.navigate([this.workflowNavigationService.getFirstAvailableExitURL(wfsItem, EWorkflowPathKey.OVERVIEW)])
			.catch(console.error);
	}
	
	private parseTransitionToEndState(transition: CustomStateTransition, states: CustomState[]): { label: string, symbol: string, id: string, } {
		const endState = states?.find(state => state.id === transition.endStateId);
		return {
			label: transition.label,
			symbol: endState?.symbol,
			id: endState.id,
		};
	}
	
	private mapWfsItemStatusToElements(isCompletable: boolean, wfsItem: IWorkflowItem, possibleTransitions: CustomStateTransition[]): SplitButtonElement[] | null {
		if (isCompletable) {
			const el = { ...this.fallBackElement };
			el.title = wfsItem?.config?.primaryButtonLabel || 'workflow.end';
			el.callback = () => this.doCompletion(wfsItem);
			
			return [el];
		}
		
		if (possibleTransitions?.length) {
			return possibleTransitions
				.filter(t => !!t)
				.map((transition): SplitButtonElement =>{
					const endState = wfsItem.customStates?.find(state => state.id === transition.endStateId);
					return {
						title: transition.label,
						icon: endState?.symbol || null,
						iconType: 'nzicon',
						type: 'primary',
						callback: () => this.doTransition(transition),
					};
				});
		}
		
		return [];
	}
	
	private async confirm(wfsItem: IWorkflowItem):Promise<boolean>{
		const doNotUseConfirmDialog = !!wfsItem?.config?.disableConfirmDialog;
		if (doNotUseConfirmDialog) { return true; }
		
		return await this.confirmDialog.prompt({
			title: await this.translateService.get('workflow.modal.finish.title').toPromise(),
			text: await this.translateService.get(wfsItem?.config?.confDialog?.textMessage ?? 'workflow.modal.finish.text').toPromise(),
			yesText: wfsItem?.config?.confDialog?.textOK,
		});
	}
	
	private async doCompletion(wfsItem: IWorkflowItem): Promise<void>{
		try {
			if (await this.confirm(wfsItem)) {
				await this.workflowItemDirective.finishWorkflow();
				await this.router.navigate([participantPages.stammDaten.base()]);
			}
		} catch (error) {
			console.error(error);
		}
	}
	
	private async doTransition(transition: CustomStateTransition): Promise<void> {
		try {
			await this.workflowItemDirective.doTransition(transition);
			await this.router.navigate([participantPages.stammDaten.base()]);
		} catch (error) {
			console.error(error);
		}
	}
	
}
